/**
 * Looking ahead, for when the screens are gone and the day has to run from paper: the next few
 * rounds as they would be drawn if nobody arrives, leaves or takes a break. Americano only: its
 * draw needs who is here and who has played whom, not the scores. A Mexicano draw follows the
 * board, so only the round already drawn can be promised.
 */

import { draftRound } from "./scheduler";
import { availablePlayers, history } from "./selectors";
import { apply } from "./state";
import type { Config, Lineup, LoggedEvent, TournamentEvent, TournamentState } from "./types";
import { actualSlotMs, dayPlan, startWaitMs, type DayPlan } from "./plan";

export function lookAhead(state: TournamentState, rounds: number): Lineup[] {
  if (state.mode === "mexicano") return state.draft ? [structuredClone(state.draft)] : [];
  let s = structuredClone(state);
  let at = Math.max(s.clock.phaseEndsAt ?? 0, ...s.rounds.map((r) => r.startedAt), 0) + 1;
  const step = (event: TournamentEvent): void => {
    at += 1;
    s = apply(s, { ...event, seq: s.seq + 1, at, by: "lookahead" } as LoggedEvent);
  };
  const out: Lineup[] = [];
  try {
    if (s.pausedAll) step({ type: "pause.all", on: false });
    if (s.clock.phase === "playing") step({ type: "round.finished" });
    if (s.clock.phase === "changeover") step({ type: "round.closed" });
    for (let i = 0; i < rounds; i += 1) {
      const lineup = i === 0 && s.draft ? s.draft : draftRound(s);
      if (!lineup.courts.length) break;
      out.push(structuredClone(lineup));
      step({ type: "round.started", lineup });
      step({ type: "round.finished" });
      step({ type: "round.closed" });
    }
  } catch {
    // whatever was drawn before the rules said no is still worth printing
  }
  return out;
}

/**
 * The day's plan as it would be with other slot times: what "8 minute games" buys. The wait for
 * an admin's Start, as measured, stays as it is: shorter games do not make the courts ready sooner.
 */
export function planWith(state: TournamentState, now: number, patch: Partial<Pick<Config, "gameMs" | "changeoverMs">>): DayPlan {
  return dayPlan({ ...state, config: { ...state.config, ...patch } }, now, startWaitMs(state));
}

export { actualSlotMs };

export interface Pace {
  /** About how many games each player here will have by the last whistle. */
  games: number;
  /** Rounds that fit before then at this pace, not counting one on court now. */
  rounds: number;
  /** The last whistle. */
  by: number;
  /** The slot the sum used, as the wall counts it: the game, the changeover and the measured wait for Start. */
  slotMs: number;
}

/**
 * "At this pace, about 9 games each by 17:45" (decision 42): the rounds that fit before the last
 * whistle, each giving as many games as the courts and the people here allow, shared among
 * everyone here on top of what they have now. The rounds are the wall's own count (dayPlan), so
 * the pace line and "round 6 of about 16" agree, rounds that finish early included. The one
 * difference: a lunch with a time to be back starts the pace line's count from then. Null without
 * a last whistle or four people here.
 */
export function paceGames(state: TournamentState, now: number): Pace | null {
  const by = state.config.lastWhistleAt;
  const here = availablePlayers(state);
  if (by === null || here.length < state.config.playersPerCourt) return null;
  const { gameMs, changeoverMs, courts, playersPerCourt } = state.config;
  const slotMs = gameMs + changeoverMs + startWaitMs(state);
  const plan = dayPlan(state, now);
  let rounds = plan.roundsLeft;
  // back from a lunch later than the plan's next start: count from then, at the same slot
  if (state.pausedAll && state.pausedUntil !== null && plan.nextRoundStartsAt !== null) {
    const next = Math.max(plan.nextRoundStartsAt, state.pausedUntil);
    rounds = next + gameMs > by ? 0 : Math.floor((by - gameMs - next) / slotMs) + 1;
  }
  const places = Math.min(courts, Math.floor(here.length / playersPerCourt)) * playersPerCourt;
  const played = history(state).gamesPlayed;
  const sofar = here.reduce((sum, id) => sum + (played[id] ?? 0), 0);
  return { games: Math.round((sofar + rounds * places) / here.length), rounds, by, slotMs };
}
